import type { ClientSectionKey, StaffRole } from "./model";
import "./missing-output.css";

/**
 * B2 (audit F115, 2026-08-11): a closed step with a missing output was the
 * worst dead end in the console, and it stood on three of four live clients
 * for eleven days.
 *
 * Two surfaces said opposite things about the same fact. The daily queue told
 * the partner "Krok Trénink je zablokovaný, dokonči chybějící výstup"; the step
 * itself, one screen over, ended with "nic dalšího tu dělat nemusíš" - and
 * carried NOT ONE button. Nobody was wrong on purpose: the partner genuinely
 * cannot fix it (the raw resource editor is admin-only, and the partner's page
 * does not even contain the words "Editor zdroje"). What was missing is the
 * sentence naming WHO fixes it and WHERE, and, for the person who actually
 * can, the way there.
 *
 * So this notice sits under the step's own alert: one line naming the place,
 * and for an admin a control that opens it. The queue's wording was corrected
 * in the same round, so the two surfaces now describe the same job.
 */
export function MissingOutputNotice(props: {
  /** The step's own sentence about what is missing (kept per step). */
  messageKey: string;
  step: string;
  role: StaffRole;
  t: (key: string) => string;
  /**
   * F138a (parity row 7): the destination moved from the Zdroje TAB to the
   * Nastavení SECTION, because the tab is gone and the resource editor lives
   * in that section now. The control does the same job through a new door.
   */
  onOpenSection?: (section: ClientSectionKey) => void;
}) {
  const admin = props.role === "admin";
  return (
    <div className="staff-missing-output" data-missing-output={props.step}>
      <p className="message error-message completionProblem" role="alert">
        {props.t(props.messageKey)}
      </p>
      <p className="staff-card-hint">
        {props.t(admin
          ? "staffConsole.missingOutput.adminWhere"
          : "staffConsole.missingOutput.partnerWhere")}
      </p>
      {/* Only the admin gets a control, because only the admin's own screen
          has the editor behind it. A partner would get a button leading to a
          panel that is not there - the P-18 mistake in reverse. */}
      {admin && props.onOpenSection && (
        <div className="button-row">
          <button
            className="secondary-button compact-button"
            type="button"
            onClick={() => props.onOpenSection?.("settings")}
          >
            {props.t("staffConsole.missingOutput.openResources")}
          </button>
        </div>
      )}
    </div>
  );
}
